import React, { useState, useRef, useEffect } from 'react';
import { legalQaService } from '../../services/api';
import { getAppMarket } from '../../services/MarketService';

interface Citation {
    act: string;
    section?: string;
    summary: string;
}

interface EscalationWarning {
    triggerId: string;
    severity: string;
    message: string;
    requiresReferral: boolean;
}

interface QAResponse {
    answer: string;
    citations?: Citation[];
    disclaimer?: string;
    confidence?: string;
    followUpQuestions?: string[];
    escalationWarnings?: EscalationWarning[];
    processingTimeMs?: number;
    model?: string;
}

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    citations?: Citation[];
    escalationWarnings?: EscalationWarning[];
    confidence?: string;
    processingTimeMs?: number;
}

const QAChat: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: 'welcome',
            role: 'assistant',
            content: 'Hello there, how can we help you today?'
        }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<Record<string, 'yes' | 'no' | null>>({});
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleFeedback = (messageId: string, value: 'yes' | 'no') => {
        setFeedback(prev => ({ ...prev, [messageId]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMessage: Message = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: input.trim()
        };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);
        setError(null);

        try {
            const response = await legalQaService.ask(input.trim());
            const data: QAResponse = response.data?.data || response.data;

            const assistantMessage: Message = {
                id: `assistant-${Date.now()}`,
                role: 'assistant',
                content: data.answer,
                citations: data.citations,
                escalationWarnings: data.escalationWarnings,
                confidence: data.confidence,
                processingTimeMs: data.processingTimeMs
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (err: any) {
            console.error('[LegalQA] Error:', err);
            setError(err?.response?.data?.message || err.message || 'Failed to get answer');
        } finally {
            setLoading(false);
        }
    };

    const getConfidenceBadge = (confidence?: string) => {
        if (!confidence) return null;
        const colors: Record<string, string> = {
            HIGH: 'bg-green-100 text-green-800',
            MEDIUM: 'bg-yellow-100 text-yellow-800',
            LOW: 'bg-red-100 text-red-800'
        };
        return (
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${colors[confidence] || 'bg-gray-100 text-gray-600'}`}>
                {confidence} confidence
            </span>
        );
    };

    return (
        <div className="flex flex-col h-[70vh] bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                            msg.role === 'user'
                                ? 'bg-green-600 text-white rounded-br-md'
                                : 'bg-gray-50 text-gray-800 border border-gray-200 rounded-bl-md'
                        }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {msg.role === 'assistant' && (
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {getConfidenceBadge(msg.confidence)}
                                        {msg.processingTimeMs && (
                                            <span className="text-xs text-gray-400">{(msg.processingTimeMs / 1000).toFixed(1)}s</span>
                                        )}
                                    </div>
                                    {msg.citations && msg.citations.length > 0 && (
                                        <div className="border-t border-gray-200 pt-2 mt-2">
                                            <p className="text-xs font-semibold text-gray-500 mb-1">Citations</p>
                                            {msg.citations.map((c, i) => (
                                                <p key={i} className="text-xs text-gray-500">
                                                    {c.act}{c.section ? `, ${c.section}` : ''} — {c.summary}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                    {msg.escalationWarnings && msg.escalationWarnings.length > 0 && (
                                        <div className="border-t border-gray-200 pt-2 mt-2">
                                            {msg.escalationWarnings.map((w, i) => (
                                                <div key={i} className={`text-xs p-2 rounded-md ${
                                                    w.severity === 'HIGH'
                                                        ? 'bg-red-50 text-red-700 border border-red-200'
                                                        : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                                }`}>
                                                    <span className="font-semibold">
                                                        {w.severity === 'HIGH' ? 'Important' : 'Note'}:
                                                    </span>{' '}
                                                    {w.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {msg.id !== 'welcome' && (
                                        <div className="border-t border-gray-200 pt-2 mt-2 flex items-center gap-3">
                                            <span className="text-xs text-gray-400">Did we answer your question?</span>
                                            <button
                                                onClick={() => handleFeedback(msg.id, 'yes')}
                                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                                    feedback[msg.id] === 'yes'
                                                        ? 'bg-green-100 text-green-700 border-green-300'
                                                        : 'bg-white text-gray-500 border-gray-300 hover:bg-green-50 hover:border-green-300'
                                                }`}
                                            >
                                                Yes
                                            </button>
                                            <button
                                                onClick={() => handleFeedback(msg.id, 'no')}
                                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                                    feedback[msg.id] === 'no'
                                                        ? 'bg-red-100 text-red-700 border-red-300'
                                                        : 'bg-white text-gray-500 border-gray-300 hover:bg-red-50 hover:border-red-300'
                                                }`}
                                            >
                                                No
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="bg-gray-50 border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3">
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                        </div>
                    </div>
                )}
                {error && (
                    <div className="flex justify-center">
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">
                            {error}
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-gray-200 p-4">
                <form onSubmit={handleSubmit} className="flex items-center space-x-3">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={getAppMarket() === 'texas' ? 'Ask about US (Texas) housing law...' : 'Ask about UK housing law...'}
                        disabled={loading}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                    />
                    <button
                        type="submit"
                        disabled={loading || !input.trim()}
                        className="bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? '...' : 'Ask'}
                    </button>
                </form>
                <p className="text-xs text-gray-400 mt-2">
                    Responses are AI-generated and for general informational purposes only. Not legal advice.
                </p>
            </div>
        </div>
    );
};

export default QAChat;
